import React, { useRef } from "react";
import "./ImageUploader.scss";
import classNames from "classnames";
import { ReactComponent as UploadIcon } from "src/images/upload-file.svg";
import { ReactComponent as ThrashcanIcon } from "src/images/trash-can-gray.svg";
import { ReactComponent as ReplaceIcon } from "src/images/replace.svg";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import { dataURLtoFile } from "../CroppedImageUploader/utils/dataURLtoFile";

interface Props {
  imageUrl: string | null;
  className?: string;
  maxFileSize?: number;
  formats?: string;
  uploadFunction: (file: File) => void;
  removeFunction: () => void;
}

function ImageUploader(props: Props) {
  const {
    imageUrl,
    className,
    maxFileSize,
    formats,
    uploadFunction,
    removeFunction,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  function onChange(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const { files } = changeEvent.target;
    if (!files?.length) {
      return;
    }
    const file = files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function (loadEvent) {
      const newFile = dataURLtoFile(reader.result, "ws_cover.png");
      uploadFunction(newFile);
    };
  }

  const renderPlaceholder = () => {
    if (imageUrl) return null;

    return (
      <div className="image-uploader__placeholder">
        <input
          type="file"
          className="image-uploader__input"
          onChange={onChange}
          accept="image/*"
          ref={inputRef}
        />

        <UploadIcon />
        <IDHFormattedMessage
          id="ws_upload_image"
          defaultMessage="Upload image"
        />
        <div>
          {maxFileSize && (
            <>
              <IDHFormattedMessage
                id="ws_max_file_size"
                defaultMessage="Max file size"
              />
              : {maxFileSize}MB
            </>
          )}

          {formats && (
            <>
              <IDHFormattedMessage id="ws_formats" defaultMessage="Formats" />:{" "}
              {formats}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderImage = () => {
    if (!imageUrl) return null;

    return (
      <>
        <input
          type="file"
          className="image-uploader__hidden-input"
          onChange={onChange}
          accept="image/*"
          ref={inputRef}
        />
        <div className="image-uploader__buttons">
          <span onClick={() => inputRef.current?.click()}>
            <ReplaceIcon />
            <IDHFormattedMessage
              id="ws_change_image"
              defaultMessage="Change image"
            />
          </span>
          ・
          <span onClick={removeFunction}>
            <ThrashcanIcon />
            <IDHFormattedMessage
              id="ws_remove_image"
              defaultMessage="Remove image"
            />
          </span>
        </div>
        <div className="image-uploader__image-wrapper">
          <img
            src={imageUrl}
            alt="uploaded image"
            className="image-uploader__image"
          />
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (imageUrl) {
      return renderImage();
    }

    return renderPlaceholder();
  };

  return (
    <div
      className={classNames(
        "image-uploader",
        {
          "image-uploader--no-border": Boolean(imageUrl),
        },
        className,
      )}
    >
      {renderContent()}
    </div>
  );
}

export default ImageUploader;
